import { getImageSizeFromUrl } from '@Components/BigBrowse/BBCalculationsUtils';
import { RoundedRectangleGeometry } from '@Components/BigBrowse/GridCardMesh/Components/Shapes/RoundedRectangleGeometry';
import type { Position2D } from '@Components/BigBrowse/Model/ModelTypes';
import { useDebouncedClick } from '@Components/BigBrowse/bb2/meshes2/primitives/primitives.common';
import type { Assets } from '@Utils/AssetsStore';
import { useAssetsStore } from '@Utils/AssetsStore';
import { waitForMs } from '@Utils/miscUtils';
import { useOnHoverMousePointer } from '@Utils/utilStyles';
import { a, useSpring } from '@react-spring/three';
import { computed } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { useBbContentPresenter } from '../../BbContentPresenter';
import { useHeroLoadingTracker } from '../../HeroLoadingTracker';
import { ImageMaterial2 } from './ImageMaterial2';
import { ImageMaterialZoomAble } from './ImageMaterialZoomable';

type Props = {
  size: { width: number; height: number };
  position: Position2D;
  imageUrl?: string;
  onClick?: () => void;
  transparencyColor?: string;
  children?: ReactNode;
  hoverDisabled?: boolean;
  uniqueKey: string;
  isViewHero?: boolean;
  shadow?: { asset: Assets; bmpSize: number; scale: number };
  opacity?: number;
  radius?: number;
  zRange?: [number, number];
  animationTime?: number;
  animatedIn?: boolean;
};

export const ProductCardRectZoom = observer(function ProductCardRectZoom({
  size,
  position,
  onClick: onClickCallback,
  imageUrl,
  transparencyColor,
  children,
  hoverDisabled = false,
  uniqueKey,
  isViewHero,
  shadow,
  opacity = 1,
  radius = 15,
  zRange = [0, 0.1],
  animationTime = 1000,
  animatedIn,
}: Props) {
  const [onIn, onOut] = useOnHoverMousePointer(
    Boolean(onClickCallback) && !hoverDisabled
  );

  const bbContentPresenter = useBbContentPresenter();
  const { loaded } = useHeroLoadingTracker();
  const { get, loaded: assetsLoaded } = useAssetsStore();

  const itemTransforms = computed(() =>
    bbContentPresenter.itemTransforms.get(uniqueKey)
  ).get();

  const { onPointerUp, onPointerDown, onClick } =
    useDebouncedClick(onClickCallback);

  const { xAnim } = useSpring({
    to: { xAnim: position.x + (itemTransforms?.position?.to.x ?? 0) },
    config: { duration: animationTime },
  });

  const { yAnim } = useSpring({
    ...(itemTransforms?.position?.from
      ? { from: { yAnim: position.y + itemTransforms?.position?.from.y } }
      : {}),
    to: { yAnim: position.y + (itemTransforms?.position?.to.y ?? 0) },
    config: { duration: animationTime },
  });

  const bmpSize = useMemo(() => getImageSizeFromUrl(imageUrl), [imageUrl]);

  const [scale, setScale] = useState(1);
  const combinedScale = scale * (itemTransforms?.scale?.to ?? 1);

  const { scaleAnim } = useSpring({
    to: { scaleAnim: combinedScale },
    config: { duration: 500 },
  });

  const onImageLoad = async () => {
    await waitForMs(750); // wait for image to load and also animation finish + 250ms
    loaded.set(bbContentPresenter.paramsSignature, true);
  };

  const onPointerOver = () => {
    if (hoverDisabled) {
      return;
    }
    setScale(1.1);
    onIn();
  };

  const onPointerOut = () => {
    if (hoverDisabled) {
      return;
    }
    setScale(1);
    onOut();
  };

  const [zMin, zMax] = zRange;
  const zSpan = zMax - zMin;

  return (
    <a.group
      position-x={xAnim}
      position-y={yAnim}
      position-z={zMin}
      scale={scaleAnim}
    >
      {Boolean(shadow) && assetsLoaded && (
        // Shadow is a separate static BMP plane. It should be larger than the
        // 'content' rectangle but its "white" area should be small enough to hide
        // behind the 'content' plane
        <mesh position={[0, 0, zMin + zSpan * 0.1]} scale={shadow?.scale ?? 1}>
          <RoundedRectangleGeometry
            width={size.width}
            height={size.height}
            radius={0}
          />
          <ImageMaterial2
            imageUrl={get(shadow!.asset).uri}
            size={size.width}
            bmpSize={shadow!.bmpSize}
            opacity={opacity}
          />
        </mesh>
      )}
      <mesh
        position-x={0}
        position-y={0}
        position-z={zMin + zSpan * 0.2} // mesh should be above the shadow image below
        onPointerOver={onPointerOver}
        onPointerOut={onPointerOut}
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        frustumCulled={false}
      >
        <RoundedRectangleGeometry
          width={size.width}
          height={size.height}
          radius={radius}
        />
        {imageUrl ? (
          <ImageMaterialZoomAble
            imageUrl={imageUrl}
            size={Math.max(size.width, size.height)}
            bmpSize={bmpSize}
            transparencyColor={transparencyColor}
            onImageLoad={isViewHero ? onImageLoad : undefined}
            opacity={opacity}
            animatedIn={animatedIn}
          />
        ) : (
          <meshBasicMaterial color='white' />
        )}
        {children}
      </mesh>
    </a.group>
  );
});
